/**
 * Wraps a promise with a timeout to prevent indefinite hangs.
 *
 * Use this around any async call that might hang (Firestore getDocs, fetch, etc.)
 * so the caller gets a rejection instead of waiting forever.
 *
 * @param {Promise} promise - The promise to wrap
 * @param {number} [ms=15000] - Timeout in milliseconds
 * @param {string} [label='async operation'] - Label for the error message
 * @returns {Promise} Resolves with the original value, or rejects on timeout
 *
 * @example
 * import { withTimeout } from '@utils/withTimeout';
 * const snapshot = await withTimeout(getDocs(query), 15000, 'mentions query');
 */
export const withTimeout = (promise, ms = 15000, label = 'async operation') => {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error(`${label} timed out after ${ms / 1000}s`)),
      ms
    );
  });
  return Promise.race([promise, timeoutPromise]).finally(() => clearTimeout(timeoutId));
};
